/**
 * System-wide date formatting: dd-mmm-yyyy (e.g. 19-Feb-2025).
 * Use for all date displays; use formatDateTimeDDMMMYYYY when time is also shown.
 */

const MONTHS_MMM = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date as dd-mmm-yyyy (e.g. 19-Feb-2025).
 * @param {Date|string|number} date - Date value
 * @returns {string} Formatted date or '—' if invalid
 */
export function formatDateDDMMMYYYY(date) {
  if (date == null) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS_MMM[d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Format a date with time as dd-mmm-yyyy HH:MM (e.g. 19-Feb-2025 14:30).
 * For UTC dates, pass a Date object; it will be formatted in local time unless you use UTC getters.
 * @param {Date|string|number} date - Date value
 * @param {boolean} useUTC - If true, use UTC components (for server timestamps)
 * @returns {string} Formatted date-time or '—' if invalid
 */
export function formatDateTimeDDMMMYYYY(date, useUTC = false) {
  if (date == null) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(useUTC ? d.getUTCDate() : d.getDate()).padStart(2, '0');
  const month = MONTHS_MMM[useUTC ? d.getUTCMonth() : d.getMonth()];
  const year = useUTC ? d.getUTCFullYear() : d.getFullYear();
  const h = String(useUTC ? d.getUTCHours() : d.getHours()).padStart(2, '0');
  const m = String(useUTC ? d.getUTCMinutes() : d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${h}:${m}`;
}

/**
 * Parse offset string (e.g. "+05:30", "+05:30:00", "-05:00") to minutes from UTC.
 * @param {string|null|undefined} value
 * @returns {number} Minutes (e.g. 330 for +05:30, -300 for -05:00)
 */
export function parseOffsetToMinutes(value) {
  if (value == null || String(value).trim() === '') return 330;
  let s = String(value).trim();
  if (s.endsWith(':00') && s.length >= 6) s = s.slice(0, -3);
  const match = s.match(/^([+-])(\d{1,2}):(\d{2})$/);
  if (!match) return 330;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const mins = parseInt(match[3], 10);
  return sign * (hours * 60 + mins);
}

/**
 * Format "Performed on UTC" as local time (UTC + offset): e.g. UTC 12:48 with offset +05:30 -> "25-Feb-2026 18:18".
 * Used everywhere in the system for Performed on UTC display.
 * @param {Date|string|number|null|undefined} utcDate - UTC timestamp from server
 * @param {string|null|undefined} offsetStr - e.g. "+05:30", "+05:30:00" (parseOffsetToMinutes default 330)
 * @returns {string} Formatted "dd-mmm-yyyy HH:mm" in local (UTC+offset) or '—' if invalid
 */
export function formatPerformedOnUtcToLocal(utcDate, offsetStr) {
  if (utcDate == null) return '—';
  const d = new Date(utcDate);
  if (Number.isNaN(d.getTime())) return '—';
  const offsetMinutes = parseOffsetToMinutes(offsetStr);
  const local = new Date(d.getTime() + offsetMinutes * 60 * 1000);
  const day = String(local.getUTCDate()).padStart(2, '0');
  const month = MONTHS_MMM[local.getUTCMonth()];
  const year = local.getUTCFullYear();
  const h = String(local.getUTCHours()).padStart(2, '0');
  const m = String(local.getUTCMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${h}:${m}`;
}

/**
 * Canonical offset for display/export: no seconds, hour zero-padded (e.g. "+05:30", "-05:00").
 * Used for Performed At Offset next to Performed on UTC and report headers.
 * @param {string|null|undefined} value - Raw offset (e.g. "+05:30:00", "+5:30", "+05:30")
 * @returns {string} "+HH:mm" / "-HH:mm" or "—" if empty; unparseable strings returned trimmed as-is
 */
export function formatOffsetForDisplay(value) {
  if (value == null || String(value).trim() === '') return '—';
  let s = String(value).trim();
  if (s.endsWith(':00') && s.length >= 6) s = s.slice(0, -3);
  const match = s.match(/^([+-])(\d{1,2}):(\d{2})$/);
  if (!match) return s;
  const [, sign, h, m] = match;
  const hour = parseInt(h, 10);
  if (Number.isNaN(hour) || m.length !== 2) return s;
  return `${sign}${String(hour).padStart(2, '0')}:${m}`;
}

/** Today's calendar date as yyyy-MM-dd in local timezone (for HTML date inputs). */
export function todayIsoDateLocal() {
  const t = new Date();
  const y = t.getFullYear();
  const mo = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/**
 * Format yyyy-MM-dd as dd-MMM-yyyy without timezone shift (Issue Review Closing Date display).
 * @param {string} iso - e.g. 2026-04-18
 * @returns {string} e.g. 18-Apr-2026, or empty if invalid
 */
export function formatIsoDateCalendarDDMMMYYYY(iso) {
  if (iso == null || String(iso).trim() === '') return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  if (!m) return '';
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || day < 1 || day > 31) return '';
  return `${String(day).padStart(2, '0')}-${MONTHS_MMM[mo - 1]}-${y}`;
}

/**
 * API DateTime (ISO string, e.g. from JSON) to yyyy-MM-dd using local calendar — for HTML date input min/max.
 * @param {Date|string|number|null|undefined} value
 * @returns {string} yyyy-MM-dd or '' if invalid / empty
 */
export function apiDateTimeToIsoDateLocal(value) {
  if (value == null || String(value).trim() === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/**
 * Bounds for Issue Review "Closing Date": not after today; not before date of issue when known.
 * If stored issue date is after today (bad data), min is clamped to today so the native picker stays valid.
 * @param {Date|string|number|null|undefined} dateOfIssueRaw — API dateOfIssue / dDateOfIssue
 * @returns {{ minIso: string, maxIso: string }} minIso may be '' when date of issue is unknown
 */
export function getActionItemClosingDateBounds(dateOfIssueRaw) {
  const maxIso = todayIsoDateLocal();
  const issueIso = apiDateTimeToIsoDateLocal(dateOfIssueRaw);
  const hasIssue = issueIso && /^\d{4}-\d{2}-\d{2}$/.test(issueIso);
  if (!hasIssue) return { minIso: '', maxIso };
  const minIso = issueIso > maxIso ? maxIso : issueIso;
  return { minIso, maxIso };
}

/**
 * Clamp yyyy-MM-dd within optional min/max (inclusive). Empty min/max means no bound.
 * @param {string} iso
 * @param {string} minIso
 * @param {string} maxIso
 * @returns {string}
 */
export function clampIsoDateToMinMax(iso, minIso, maxIso) {
  const raw = iso == null ? '' : String(iso).trim();
  if (raw === '') return '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  let v = raw;
  if (minIso && /^\d{4}-\d{2}-\d{2}$/.test(minIso) && v < minIso) v = minIso;
  if (maxIso && /^\d{4}-\d{2}-\d{2}$/.test(maxIso) && v > maxIso) v = maxIso;
  return v;
}
